'use client'
import { useContext, useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMouseMovement } from "@/app/[lang]/MouseMovementContext";
import { useArticleLayout } from "@/lib/providers/article-layout-provider";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import ArticleCardDefault from "@/components/share-format/article-card-default";
import "./article-carousel.css";

interface ArticleCarouselProps {
    articles: any[];
    priorityImage?: boolean;
    loc: string;
}


const ArticleCarousel = (props: ArticleCarouselProps) => {
    const { articles, priorityImage, loc } = props;

    const articleLayout = useArticleLayout();
    const maxItem = articleLayout?.articleCarouselColumns || 4;
    const arrowPosition = articleLayout?.arrowPosition || "BOTTOM";
    const colorStyle = useContext(ColorStyleContext);
    const { hasMouseMoved } = useMouseMovement();

    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, dragFree: true });
            
    const [prevDisabled, setPrevDisabled] = useState(true);
    const [nextDisabled, setNextDisabled] = useState(true);
    const [hasOverflow, setHasOverflow] = useState(false); // determines if buttons should display

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        const canPrev = emblaApi.canScrollPrev();
        const canNext = emblaApi.canScrollNext();
        setPrevDisabled(!canPrev);
        setNextDisabled(!canNext);
        // Overflow exists if either direction can scroll
        setHasOverflow(canPrev || canNext);
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onSelect);
    }, [emblaApi, onSelect]);

    const scrollNext = () => {
        if (emblaApi) emblaApi.scrollNext();
    };
    const scrollPrev = () => {
        if (emblaApi) emblaApi.scrollPrev();
    };

    return (
        <div id="article-carousel-container" className="w-full relative">
            <div id="article-carousel-embla" className="embla" ref={emblaRef}>
                <div 
                    id="article-carousel-embla-container" 
                    className="embla__container" 
                    data-items-per-view={maxItem}
                    style={{ 
                        display: "flex", 
                        gap: "16px",
                        // Expose items-per-view to CSS via variable for dynamic width calc
                        ['--items-per-view' as any]: maxItem
                    }}
                >    
                    {articles.map((article, index) => {
                        let formattedDate = "";
                        if (hasMouseMoved) {
                            const date = new Date(article.publishedAt);
                            formattedDate = date.toLocaleString(loc == "TH" ? "th-TH" : "en-GB", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            });
                        }
                        return (
                            <ArticleCardDefault 
                                key={index} 
                                articleItem={article} 
                                priorityImage={priorityImage} 
                                formattedDate={formattedDate}
                                loc={loc}
                            />
                        );
                    })}
                </div>
            </div>
            {hasOverflow && (
                <div id="article-carousel-prev-next" className="w-full flex justify-end mt-[20px]">
                    <button
                        id="article-carousel-prev-button"
                        aria-label="Previous" 
                        className={`
                            embla__button embla__button--prev inline-flex items-center justify-center 
                            whitespace-nowrap ring-offset-background transition-colors focus:outline-none disabled:pointer-events-none 
                            disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full max-md:hidden 
                            dynamic-bg dynamic-color dynamic-border ${arrowPosition == "SIDE" ? "absolute left-0 top-1/2 -translate-y-[170%] -translate-x-1/2 shadow" : "relative transform left-0 top-0 translate-y-0 mx-1" }`}
                        style={
                            {
                                "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                "--dynamic-border": colorStyle?.buttonTextNormal || "#ffffff",
                                "--dynamic-hover-border": colorStyle?.buttonTextHover || "#ffffff",
                                "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                            } as React.CSSProperties
                        }
                        onClick={scrollPrev}
                        disabled={prevDisabled}
                    >
                        <ChevronLeft 
                            className="h-5 w-5"
                        />
                    </button>
                    <button
                        id="article-carousel-next-button"
                        aria-label="Next"
                        className={`
                            embla__button embla__button--next inline-flex items-center justify-center 
                            whitespace-nowrap ring-offset-background transition-colors focus:outline-none disabled:pointer-events-none 
                            disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full -right-12 max-md:hidden 
                            dynamic-bg dynamic-color dynamic-border ${arrowPosition == "SIDE" ? "absolute right-0 top-1/2 -translate-y-[170%] translate-x-1/2 shadow" : "relative transform left-0 top-0 translate-y-0 mx-1"}`}
                        style={
                            {
                                "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                "--dynamic-border": colorStyle?.buttonTextNormal || "#ffffff",
                                "--dynamic-hover-border": colorStyle?.buttonTextHover || "#ffffff",
                                "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                            } as React.CSSProperties
                        }
                        onClick={scrollNext}
                        disabled={nextDisabled}
                    >
                        <ChevronRight 
                            className="h-5 w-5"
                        />
                    </button>
                </div>
            )}
        </div>
    )
}

export default ArticleCarousel